/**
* Creates a targeted event listener on `window` that only fires when the
* event's `detail.id` matches the element's `id` attribute.
*
* This enables selective communication between HTMX responses (via HX-Trigger)
* and specific Alpine.js components, e.g. closing one modal without affecting others.
*
* HX-Trigger format expected:
    *   Single target:   {"close-modal": {"id": "modalA"}}
*   Multiple targets: {"close-modal": {"id": ["modalA", "modalC"]}}
*
* @param {string} event - The event name to listen for (e.g. 'close-modal')
* @param {HTMLElement} elem - The DOM element whose `id` attribute is used for matching
                                                                               * @param {function(Event): void} handler - Callback invoked when the element is targeted
* @returns {function(): void} Cleanup function that removes the listener
*
* @example
*   // In an Alpine component:
*   init() {
*     this._cleanup = createEventListener('close-modal', this.$el, () => {
    *       this.open = false;
    *     });
*   },
*   destroy() {
*     this._cleanup?.();
*   }
*/
export function createEventListener(event, elem, handler) {
    const id = elem.getAttribute('id');

    // Add X-Source-Id header to HTMX config requests so the backend knows which component triggered the request and can target it in the response
    elem.addEventListener('htmx:configRequest', (event) => {
        event.detail.headers['X-Source-Id'] = id;
    });

    const listener = (e) => {
        const target = e.detail?.value;
        if (target === id
            || (Array.isArray(target) && target.includes(id))) {
            handler(e);
        }
    };

    window.addEventListener(event, listener);

    return () => window.removeEventListener(event, listener);
}


/**
 * Registers multiple targeted event listeners on a single element.
 *
 * Convenience wrapper around {@link createEventListener} for components
 * that need to react to several HX-Trigger events.
 *
 * @param {HTMLElement} elem - The DOM element whose `id` attribute is used for matching
 * @param {Object<string, function(Event): void>} config - Map of event names to handlers
 * @returns {function(): void} Single cleanup function that removes all listeners
 *
 * @example
 *   Alpine.data('modal', () => ({
 *     open: false,
 *     loading: false,
 *     _cleanup: null,
 *
 *     init() {
 *       this._cleanup = createEventListeners(this.$el, {
 *         'close-modal':    () => { this.open = false; },
 *         'open-modal':     () => { this.open = true; },
 *         'toggle-loading': () => { this.loading = !this.loading; },
 *       });
 *     },
 *
 *     destroy() {
 *       this._cleanup?.();
 *     }
 *   }));
 */
export function createEventListeners(elem, config) {
    const cleanups = Object.entries(config).map(
        ([event, handler]) => createEventListener(event, elem, handler)
    );
    return () => cleanups.forEach(fn => fn());
}